import React, { useEffect, useState } from "react";
import {
  Truck,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  Save,
  RefreshCw,
  Sparkles,
  ShieldCheck,
  Info,
  Plus,
  Trash2,
  Layers,
  MapPin,
  Calculator,
} from "lucide-react";

const API_CANDIDATES = [
  "http://localhost:5000/shipping",
  "http://127.0.0.1:5000/shipping",
  "https://api.vanyacollectionsrajahmundry.com/shipping",
];

const ALL_INDIAN_STATES = [
  "Andhra Pradesh",
  "Telangana",
  "Karnataka",
  "Tamil Nadu",
  "Kerala",
  "Maharashtra",
  "Gujarat",
  "Rajasthan",
  "Madhya Pradesh",
  "Uttar Pradesh",
  "West Bengal",
  "Bihar",
  "Odisha",
  "Punjab",
  "Haryana",
  "Delhi",
  "Assam",
  "Goa",
  "Chhattisgarh",
  "Jharkhand",
  "Uttarakhand",
  "Himachal Pradesh",
  "Jammu and Kashmir",
  "Tripura",
  "Meghalaya",
  "Manipur",
  "Nagaland",
  "Mizoram",
  "Arunachal Pradesh",
  "Sikkim",
  "Chandigarh",
  "Puducherry",
];

const DEFAULT_STATE_RULES = [
  {
    id: "ap_tg",
    name: "Andhra Pradesh & Telangana",
    states: ["Andhra Pradesh", "Telangana"],
    rates: {
      "1": 60,
      "2": 100,
      "3": 120,
      "4": 150,
      "5": 200,
      "6": 250,
      "7": 280,
      "8": 320,
      "9": 350,
      "10": 400,
    },
  },
  {
    id: "rest_of_india",
    name: "Rest of India / Other States (Karnataka, Tamil Nadu, etc.)",
    states: [],
    is_default: true,
    rates: {
      "1": 120,
      "2": 150,
      "3": 200,
      "4": 250,
      "5": 300,
      "6": 350,
      "7": 400,
      "8": 450,
      "9": 500,
      "10": 500,
    },
  },
];

const AdminShippingSettings = () => {
  const [threshold, setThreshold] = useState("0");
  const [shippingFee, setShippingFee] = useState("60");
  const [isEnabled, setIsEnabled] = useState(true);
  const [stateRules, setStateRules] = useState(DEFAULT_STATE_RULES);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: '' }

  // Simulator state
  const [simState, setSimState] = useState("Andhra Pradesh");
  const [simPieces, setSimPieces] = useState(1);

  // --------------------------------------------------
  // LOAD SETTINGS
  // --------------------------------------------------
  const fetchSettings = async () => {
    setLoading(true);
    setMessage(null);

    // Check localStorage cache first
    try {
      const cached = localStorage.getItem("vanya_shipping_settings");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.free_shipping_threshold !== undefined) {
          setThreshold(String(parsed.free_shipping_threshold));
        }
        if (parsed.shipping_fee !== undefined) {
          setShippingFee(String(parsed.shipping_fee));
        }
        if (parsed.is_enabled !== undefined) {
          setIsEnabled(Boolean(parsed.is_enabled));
        }
        if (Array.isArray(parsed.state_rules) && parsed.state_rules.length > 0) {
          setStateRules(parsed.state_rules);
        }
      }
    } catch (e) {
      console.warn("Error reading cached shipping settings", e);
    }

    let loaded = false;
    for (const baseUrl of API_CANDIDATES) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 4000);

        const res = await fetch(`${baseUrl}/settings`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (res.ok) {
          const data = await res.json();
          if (data && data.success && data.settings) {
            setThreshold(String(data.settings.free_shipping_threshold ?? 0));
            setShippingFee(String(data.settings.shipping_fee ?? 60));
            setIsEnabled(
              data.settings.is_enabled !== undefined
                ? Boolean(data.settings.is_enabled)
                : true
            );

            if (
              Array.isArray(data.settings.state_rules) &&
              data.settings.state_rules.length > 0
            ) {
              setStateRules(data.settings.state_rules);
            }

            localStorage.setItem(
              "vanya_shipping_settings",
              JSON.stringify(data.settings)
            );
            loaded = true;
            break;
          }
        }
      } catch (err) {
        // Continue trying next candidate
      }
    }

    if (!loaded) {
      console.log("Using cached/default shipping settings");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // --------------------------------------------------
  // UPDATE STATE RATE SLAB
  // --------------------------------------------------
  const handleRateChange = (ruleIndex, pieceKey, val) => {
    setStateRules((prev) => {
      const updated = [...prev];
      const rule = { ...updated[ruleIndex] };
      rule.rates = {
        ...rule.rates,
        [pieceKey]: Number(val) >= 0 ? Number(val) : 0,
      };
      updated[ruleIndex] = rule;
      return updated;
    });
  };

  // --------------------------------------------------
  // TOGGLE STATE IN RULE
  // --------------------------------------------------
  const handleToggleStateInRule = (ruleIndex, stateName) => {
    setStateRules((prev) => {
      const updated = [...prev];
      const rule = { ...updated[ruleIndex] };
      const currentStates = Array.isArray(rule.states) ? [...rule.states] : [];

      if (currentStates.includes(stateName)) {
        rule.states = currentStates.filter((s) => s !== stateName);
      } else {
        rule.states = [...currentStates, stateName];
      }
      updated[ruleIndex] = rule;
      return updated;
    });
  };

  // --------------------------------------------------
  // ADD NEW STATE RULE
  // --------------------------------------------------
  const handleAddRule = () => {
    const newId = `rule_${Date.now()}`;
    const newRule = {
      id: newId,
      name: "New State Group",
      states: [],
      rates: {
        "1": 100,
        "2": 150,
        "3": 200,
        "4": 250,
        "5": 300,
        "6": 350,
        "7": 400,
        "8": 450,
        "9": 500,
        "10": 500,
      },
    };
    setStateRules((prev) => [newRule, ...prev]);
  };

  // --------------------------------------------------
  // DELETE STATE RULE
  // --------------------------------------------------
  const handleDeleteRule = (ruleIndex) => {
    if (stateRules[ruleIndex]?.is_default) {
      alert("The default Rest of India rule cannot be removed.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this shipping rule?")) {
      setStateRules((prev) => prev.filter((_, idx) => idx !== ruleIndex));
    }
  };

  // --------------------------------------------------
  // CALCULATE SIMULATED RATE
  // --------------------------------------------------
  const calculateSimulatedShipping = (stateName, pieces) => {
    const clean = String(stateName || "").trim().toLowerCase();
    const count = Math.min(Math.max(1, Number(pieces) || 1), 10);

    let matched = null;
    if (clean) {
      matched = stateRules.find(
        (r) =>
          !r.is_default &&
          Array.isArray(r.states) &&
          r.states.some((s) => s.toLowerCase() === clean)
      );
    }
    if (!matched) {
      matched = stateRules.find((r) => r.is_default) || stateRules[stateRules.length - 1];
    }

    const rate = matched?.rates?.[String(count)] ?? Number(shippingFee || 60);
    return {
      rate,
      ruleName: matched?.name || "Standard Default",
    };
  };

  const simResult = calculateSimulatedShipping(simState, simPieces);

  // --------------------------------------------------
  // SAVE SETTINGS
  // --------------------------------------------------
  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setMessage(null);

    const numThreshold = Number(threshold);
    const numFee = Number(shippingFee);

    if (isNaN(numThreshold) || numThreshold < 0) {
      setMessage({
        type: "error",
        text: "Please enter a valid free shipping threshold amount (>= 0).",
      });
      return;
    }

    if (isNaN(numFee) || numFee < 0) {
      setMessage({
        type: "error",
        text: "Please enter a valid standard shipping fee amount (>= 0).",
      });
      return;
    }

    setSaving(true);
    const payload = {
      free_shipping_threshold: numThreshold,
      shipping_fee: numFee,
      is_enabled: isEnabled,
      state_rules: stateRules,
    };

    let saved = false;
    for (const baseUrl of API_CANDIDATES) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const res = await fetch(`${baseUrl}/settings`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (res.ok) {
          const data = await res.json();
          if (data && data.success) {
            saved = true;
            break;
          }
        }
      } catch (err) {
        // try next candidate
      }
    }

    // Always update local cache & notify storefront
    localStorage.setItem("vanya_shipping_settings", JSON.stringify(payload));
    window.dispatchEvent(new Event("storage"));

    setSaving(false);

    setMessage({
      type: "success",
      text: saved
        ? "State & piece-wise shipping charges updated and saved to database successfully!"
        : "Shipping configuration saved locally and applied to active store checkout!",
    });

    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  return (
    <div className="shipping-container" style={styles.container}>
      {/* HEADER SECTION */}
      <div className="shipping-page-header" style={styles.pageHeader}>
        <div>
          <div style={styles.badge}>
            <Truck size={14} style={{ marginRight: 6 }} />
            STORE SHIPPING CONFIGURATION
          </div>
          <h1 className="shipping-title" style={styles.title}>Shipping Charges & Slabs Manager</h1>
          <p className="shipping-subtitle" style={styles.subtitle}>
            Configure state-specific and piece-count-based delivery fees (e.g. AP to Telangana vs. Karnataka & Other States) and free shipping threshold.
          </p>
        </div>

        <div className="shipping-header-actions" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={fetchSettings}
            disabled={loading || saving}
            style={styles.refreshBtn}
            title="Reload settings"
          >
            <RefreshCw size={16} className={loading ? "spin-icon" : ""} />
            <span>Refresh</span>
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            style={styles.headerSaveBtn}
          >
            <Save size={16} />
            <span>{saving ? "Saving..." : "Save All Rules"}</span>
          </button>
        </div>
      </div>

      {/* ALERT MESSAGE */}
      {message && (
        <div
          style={{
            ...styles.alert,
            backgroundColor: message.type === "success" ? "#ecfdf5" : "#fef2f2",
            borderColor: message.type === "success" ? "#10b981" : "#ef4444",
            color: message.type === "success" ? "#065f46" : "#991b1b",
          }}
        >
          {message.type === "success" ? (
            <CheckCircle2 size={20} style={{ flexShrink: 0 }} />
          ) : (
            <AlertCircle size={20} style={{ flexShrink: 0 }} />
          )}
          <span style={styles.alertText}>{message.text}</span>
        </div>
      )}

      <div className="shipping-main-grid" style={styles.grid}>
        {/* LEFT COLUMN: RULES CONFIGURATION */}
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* CARD 1: CART VALUE THRESHOLD */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardHeaderIcon}>
                <IndianRupee size={20} color="#183B32" />
              </div>
              <div>
                <h2 style={styles.cardTitle}>Cart Value & Free Shipping Threshold</h2>
                <p style={styles.cardDesc}>
                  Orders exceeding this threshold automatically receive 100% Free Shipping. (Set to 0 to disable).
                </p>
              </div>
            </div>

            <div style={styles.cardBody}>
              <div style={styles.switchRow}>
                <div>
                  <div style={styles.switchLabel}>Enable Free Shipping on Order Value</div>
                  <div style={styles.switchSub}>
                    When enabled, if customer cart value reaches threshold, delivery fee is waived.
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    style={styles.switchInput}
                  />
                  <span
                    style={{
                      ...styles.slider,
                      backgroundColor: isEnabled ? "#183B32" : "#cbd5e1",
                    }}
                  >
                    <span
                      style={{
                        ...styles.sliderDot,
                        transform: isEnabled ? "translateX(24px)" : "translateX(3px)",
                      }}
                    />
                  </span>
                </label>
              </div>

              <div style={styles.divider} />

              <div className="shipping-threshold-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    Free Shipping Threshold (₹)
                  </label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.currencyPrefix}>₹</span>
                    <input
                      type="number"
                      min="0"
                      value={threshold}
                      onChange={(e) => setThreshold(e.target.value)}
                      placeholder="0"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.fieldHint}>
                    {Number(threshold) > 0 ? (
                      <>Free shipping applied when subtotal &ge; <strong>₹{Number(threshold).toLocaleString()}</strong></>
                    ) : (
                      <>Threshold set to ₹0 (Piece-based charges always apply).</>
                    )}
                  </div>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    Default Standard Fallback Fee (₹)
                  </label>
                  <div style={styles.inputWrapper}>
                    <span style={styles.currencyPrefix}>₹</span>
                    <input
                      type="number"
                      min="0"
                      value={shippingFee}
                      onChange={(e) => setShippingFee(e.target.value)}
                      placeholder="60"
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.fieldHint}>
                    Fallback rate if no specific tier matches.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CARD 2: STATE & PIECE-BASED SHIPPING RULES */}
          <div style={styles.card}>
            <div
              style={{
                ...styles.cardHeader,
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={styles.cardHeaderIcon}>
                  <Layers size={20} color="#183B32" />
                </div>
                <div>
                  <h2 style={styles.cardTitle}>State-Wise & Piece-Count Rates (1 to 10+ Pieces)</h2>
                  <p style={styles.cardDesc}>
                    Define shipping amounts for 1 to 10+ dress materials / sarees for specific states vs other states.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleAddRule}
                style={styles.addRuleBtn}
              >
                <Plus size={16} />
                <span>Add State Group Rule</span>
              </button>
            </div>

            <div style={styles.cardBody}>
              {stateRules.map((rule, ruleIdx) => {
                const isDefault = rule.is_default;
                return (
                  <div
                    key={rule.id || ruleIdx}
                    style={{
                      ...styles.ruleCard,
                      borderColor: isDefault ? "#c5a059" : "#e2e8f0",
                      backgroundColor: isDefault ? "#fefdfa" : "#ffffff",
                    }}
                  >
                    {/* RULE HEADER */}
                    <div className="shipping-rule-card-header" style={styles.ruleCardHeader}>
                      <div className="shipping-rule-name-wrap" style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                        <span
                          style={{
                            ...styles.ruleTag,
                            backgroundColor: isDefault ? "#183B32" : "#4b2954",
                          }}
                        >
                          {isDefault ? "DEFAULT FALLBACK" : `RULE #${ruleIdx + 1}`}
                        </span>

                        <input
                          type="text"
                          value={rule.name || ""}
                          disabled={isDefault}
                          onChange={(e) => {
                            const val = e.target.value;
                            setStateRules((prev) => {
                              const updated = [...prev];
                              updated[ruleIdx] = { ...updated[ruleIdx], name: val };
                              return updated;
                            });
                          }}
                          placeholder="Rule Name (e.g. Andhra Pradesh & Telangana)"
                          style={styles.ruleNameInput}
                        />
                      </div>

                      {!isDefault && (
                        <button
                          type="button"
                          onClick={() => handleDeleteRule(ruleIdx)}
                          style={styles.deleteBtn}
                          title="Delete this state rule"
                        >
                          <Trash2 size={16} />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>

                    {/* STATES ASSIGNED */}
                    {!isDefault && (
                      <div style={styles.statesSection}>
                        <div style={styles.statesSectionTitle}>
                          <MapPin size={14} style={{ marginRight: 5, color: "#c5a059" }} />
                          Selected States for this rule (Click to toggle):
                        </div>
                        <div style={styles.statesChipsContainer}>
                          {ALL_INDIAN_STATES.map((stateName) => {
                            const isSelected = Array.isArray(rule.states) && rule.states.includes(stateName);
                            return (
                              <button
                                key={stateName}
                                type="button"
                                onClick={() => handleToggleStateInRule(ruleIdx, stateName)}
                                style={{
                                  ...styles.stateChip,
                                  backgroundColor: isSelected ? "#183B32" : "#f1f5f9",
                                  color: isSelected ? "#ffffff" : "#475569",
                                  borderColor: isSelected ? "#183B32" : "#cbd5e1",
                                  fontWeight: isSelected ? "700" : "500",
                                }}
                              >
                                {isSelected ? "✓ " : "+ "}
                                {stateName}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {isDefault && (
                      <div style={styles.defaultNotice}>
                        🌍 <strong>Rest of India / Other States:</strong> This rate table automatically applies to any customer whose state is not covered by any custom rule above (e.g. Karnataka, Tamil Nadu, Maharashtra, etc.).
                      </div>
                    )}

                    {/* PIECE RATES TABLE (1 to 10 Pieces) */}
                    <div style={styles.ratesTableWrapper}>
                      <div style={styles.ratesTableHeader}>
                        <span>Piece-Count Slab Shipping Rates:</span>
                      </div>
                      <div className="shipping-slabs-grid" style={styles.slabsGrid}>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
                          const key = String(num);
                          const val = rule.rates?.[key] ?? "";
                          return (
                            <div key={num} style={styles.slabBox}>
                              <div style={styles.slabLabel}>
                                {num === 10 ? "10+ Pcs" : `${num} Pc${num > 1 ? "s" : ""}`}
                              </div>
                              <div style={styles.slabInputWrapper}>
                                <span style={styles.slabPrefix}>₹</span>
                                <input
                                  type="number"
                                  min="0"
                                  value={val}
                                  onChange={(e) =>
                                    handleRateChange(ruleIdx, key, e.target.value)
                                  }
                                  style={styles.slabInput}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}

              <div style={{ marginTop: "16px" }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={styles.saveBtn}
                >
                  <Save size={18} />
                  <span>{saving ? "Saving All Changes..." : "Save Shipping Settings"}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: SIMULATOR & PREVIEW */}
        <div style={styles.sidebarColumn}>
          {/* INTERACTIVE RATE SIMULATOR */}
          <div style={styles.previewCard}>
            <div style={styles.previewHeader}>
              <Calculator size={18} color="#D4AF37" />
              <h3 style={styles.previewTitle}>Live Rate Calculator & Simulator</h3>
            </div>

            <div style={styles.previewContent}>
              <p style={{ fontSize: "12px", color: "#64748b", margin: "0 0 12px 0" }}>
                Test the checkout shipping charge calculated for any state and number of pieces.
              </p>

              <div style={styles.simField}>
                <label style={styles.simLabel}>Destination State:</label>
                <select
                  value={simState}
                  onChange={(e) => setSimState(e.target.value)}
                  style={styles.simSelect}
                >
                  {ALL_INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.simField}>
                <label style={styles.simLabel}>Number of Pieces in Cart:</label>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setSimPieces(n)}
                      style={{
                        ...styles.simPcsBtn,
                        backgroundColor: simPieces === n ? "#183B32" : "#f1f5f9",
                        color: simPieces === n ? "#ffffff" : "#334155",
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              <div style={styles.calcResultBox}>
                <div style={{ fontSize: "12px", color: "#1e293b", fontWeight: "600" }}>
                  Shipping Fee Charged:
                </div>
                <div style={styles.calcPrice}>₹{simResult.rate}</div>
                <div style={styles.calcSub}>
                  Applied Rule: <strong>{simResult.ruleName}</strong> ({simPieces} {simPieces > 1 ? "Pieces" : "Piece"})
                </div>
              </div>
            </div>
          </div>

          {/* CHECKOUT EXPERIENCE INFO */}
          <div style={styles.previewCard}>
            <div style={styles.previewHeader}>
              <Sparkles size={18} color="#D4AF37" />
              <h3 style={styles.previewTitle}>Pincode Auto-Detection</h3>
            </div>

            <div style={styles.previewContent}>
              <div style={styles.infoBox}>
                <Info size={16} color="#183B32" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div style={styles.infoText}>
                  When a customer enters a 6-digit PIN code on the checkout page, their City and State are automatically populated from India Post API.
                  The shipping fee is then instantly computed from the matching state tier rate table above!
                </div>
              </div>

              <div style={styles.previewBox}>
                <div style={styles.previewLabel}>Rule Priority at Checkout:</div>
                <ol style={{ margin: "6px 0 0 0", paddingLeft: "18px", fontSize: "12px", color: "#475569", lineHeight: "1.6" }}>
                  <li>If cart subtotal &ge; Free Shipping Threshold &gt; 0: <strong>₹0 (FREE)</strong></li>
                  <li>Destination State tier rate (1 to 10 pcs)</li>
                  <li>Rest of India / Other States tier rate (1 to 10 pcs)</li>
                </ol>
              </div>
            </div>
          </div>

          {/* AUTHORIZATION CARD */}
          <div style={styles.statusCard}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <ShieldCheck size={20} color="#10b981" />
              <div>
                <div style={{ fontSize: "13px", fontWeight: "700", color: "#183B32" }}>
                  Live Store Synchronization
                </div>
                <div style={{ fontSize: "11px", color: "#64748b" }}>
                  Changes are synchronized with customer bag, checkout, and Razorpay/COD payment totals.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE RESPONSIVE MEDIA QUERIES */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }

        @media (max-width: 992px) {
          .shipping-main-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
        }

        @media (max-width: 768px) {
          .shipping-container {
            padding: 12px 10px !important;
          }
          .shipping-page-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
            margin-bottom: 16px !important;
          }
          .shipping-title {
            font-size: 20px !important;
            line-height: 1.25 !important;
          }
          .shipping-subtitle {
            font-size: 12.5px !important;
          }
          .shipping-header-actions {
            width: 100% !important;
            justify-content: stretch !important;
            display: flex !important;
          }
          .shipping-header-actions button {
            flex: 1 !important;
            justify-content: center !important;
            padding: 10px 12px !important;
            font-size: 12.5px !important;
          }
          .shipping-threshold-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .shipping-rule-card-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
          }
          .shipping-rule-name-wrap {
            width: 100% !important;
            flex-wrap: wrap !important;
          }
          .shipping-rule-name-wrap input {
            width: 100% !important;
            min-width: 100% !important;
            font-size: 13.5px !important;
            box-sizing: border-box !important;
          }
          .shipping-slabs-grid {
            grid-template-columns: repeat(5, 1fr) !important;
            gap: 6px !important;
          }
        }

        @media (max-width: 480px) {
          .shipping-slabs-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 6px !important;
          }
          .shipping-header-actions {
            flex-direction: column !important;
          }
        }
      `}</style>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    maxWidth: "1350px",
    margin: "0 auto",
    padding: "20px 4%",
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1px",
    color: "#183B32",
    backgroundColor: "rgba(212, 175, 55, 0.2)",
    padding: "4px 10px",
    borderRadius: "20px",
    marginBottom: "8px",
  },
  title: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#183B32",
    margin: "0 0 6px 0",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    margin: 0,
    maxWidth: "700px",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    color: "#183B32",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  headerSaveBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 20px",
    backgroundColor: "#183B32",
    border: "none",
    borderRadius: "8px",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(24, 59, 50, 0.25)",
  },
  alert: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 18px",
    borderRadius: "10px",
    borderWidth: "1px",
    borderStyle: "solid",
    marginBottom: "24px",
  },
  alertText: {
    fontSize: "14px",
    fontWeight: "600",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: "24px",
    alignItems: "start",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "20px 24px",
    borderBottom: "1px solid #f1f5f9",
    backgroundColor: "#fafaf9",
  },
  cardHeaderIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    backgroundColor: "rgba(212,175,55,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: "17px",
    fontWeight: "700",
    color: "#183B32",
    margin: "0 0 3px 0",
  },
  cardDesc: {
    fontSize: "12px",
    color: "#64748b",
    margin: 0,
  },
  cardBody: {
    padding: "24px",
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  switchLabel: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#183B32",
  },
  switchSub: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "2px",
  },
  switch: {
    position: "relative",
    display: "inline-block",
    width: "52px",
    height: "28px",
    cursor: "pointer",
  },
  switchInput: {
    opacity: 0,
    width: 0,
    height: 0,
  },
  slider: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "34px",
    transition: "0.2s ease",
    display: "flex",
    alignItems: "center",
  },
  sliderDot: {
    width: "22px",
    height: "22px",
    backgroundColor: "#ffffff",
    borderRadius: "50%",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
    transition: "0.2s ease",
  },
  divider: {
    height: "1px",
    backgroundColor: "#f1f5f9",
    margin: "20px 0",
  },
  fieldGroup: {
    marginBottom: "10px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "8px",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  currencyPrefix: {
    position: "absolute",
    left: "14px",
    fontSize: "15px",
    fontWeight: "700",
    color: "#64748b",
    pointerEvents: "none",
  },
  input: {
    width: "100%",
    height: "44px",
    paddingLeft: "32px",
    paddingRight: "14px",
    fontSize: "15px",
    fontWeight: "600",
    color: "#0f172a",
    backgroundColor: "#f8fafc",
    border: "1.5px solid #cbd5e1",
    borderRadius: "8px",
    boxSizing: "border-box",
    outline: "none",
  },
  fieldHint: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "6px",
    lineHeight: "1.4",
  },
  addRuleBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    backgroundColor: "#ffffff",
    border: "1.5px dashed #c5a059",
    borderRadius: "8px",
    color: "#765c27",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
  },
  ruleCard: {
    borderRadius: "12px",
    border: "1.5px solid #e2e8f0",
    padding: "18px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
  },
  ruleCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "14px",
    flexWrap: "wrap",
  },
  ruleTag: {
    fontSize: "10px",
    fontWeight: "800",
    color: "#ffffff",
    padding: "3px 8px",
    borderRadius: "6px",
    letterSpacing: "0.5px",
  },
  ruleNameInput: {
    flex: 1,
    minWidth: "220px",
    height: "38px",
    fontSize: "15px",
    fontWeight: "700",
    color: "#183B32",
    backgroundColor: "transparent",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "0 10px",
  },
  deleteBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "6px 10px",
    backgroundColor: "#fff0f0",
    border: "1px solid #ffcaca",
    borderRadius: "6px",
    color: "#dc2626",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },
  statesSection: {
    marginBottom: "14px",
    backgroundColor: "#fbfbfc",
    border: "1px solid #eef2f6",
    borderRadius: "8px",
    padding: "12px",
  },
  statesSectionTitle: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#334155",
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
  },
  statesChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    maxHeight: "140px",
    overflowY: "auto",
    padding: "4px",
  },
  stateChip: {
    padding: "4px 8px",
    fontSize: "11px",
    borderRadius: "6px",
    border: "1px solid",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  defaultNotice: {
    backgroundColor: "#fdf8ec",
    border: "1px solid rgba(212, 175, 55, 0.4)",
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "12px",
    color: "#78350f",
    marginBottom: "14px",
    lineHeight: "1.4",
  },
  ratesTableWrapper: {
    backgroundColor: "#fafafc",
    border: "1px solid #eef1f5",
    borderRadius: "8px",
    padding: "14px",
  },
  ratesTableHeader: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#475569",
    marginBottom: "10px",
  },
  slabsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(85px, 1fr))",
    gap: "8px",
  },
  slabBox: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "8px",
    textAlign: "center",
  },
  slabLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    marginBottom: "4px",
  },
  slabInputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  slabPrefix: {
    position: "absolute",
    left: "6px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#94a3b8",
  },
  slabInput: {
    width: "100%",
    height: "32px",
    paddingLeft: "18px",
    paddingRight: "4px",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: "700",
    color: "#183B32",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    boxSizing: "border-box",
  },
  saveBtn: {
    width: "100%",
    height: "46px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    backgroundColor: "#183B32",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(24, 59, 50, 0.25)",
  },
  sidebarColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  previewCard: {
    backgroundColor: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
    border: "1px solid #e2e8f0",
    padding: "20px",
  },
  previewHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "14px",
  },
  previewTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#183B32",
    margin: 0,
  },
  previewContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  simField: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  simLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#334155",
  },
  simSelect: {
    width: "100%",
    height: "38px",
    border: "1.5px solid #cbd5e1",
    borderRadius: "8px",
    padding: "0 10px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#183B32",
    backgroundColor: "#f8fafc",
  },
  simPcsBtn: {
    flex: "1 0 16%",
    height: "32px",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
  },
  calcResultBox: {
    backgroundColor: "#f0fdf4",
    border: "1.5px solid #86efac",
    borderRadius: "10px",
    padding: "14px",
    textAlign: "center",
    marginTop: "8px",
  },
  calcPrice: {
    fontSize: "26px",
    fontWeight: "800",
    color: "#15803d",
    margin: "4px 0",
  },
  calcSub: {
    fontSize: "11px",
    color: "#166534",
  },
  previewBox: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "12px",
  },
  previewLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#183B32",
    textTransform: "uppercase",
  },
  infoBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    backgroundColor: "#fdf8ec",
    border: "1px solid rgba(212, 175, 55, 0.4)",
    padding: "12px",
    borderRadius: "8px",
  },
  infoText: {
    fontSize: "12px",
    color: "#78350f",
    lineHeight: "1.4",
  },
  statusCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid #e2e8f0",
  },
};

export default AdminShippingSettings;
